/* Renders AI summary points as a bulleted list with inline citation highlights and hover widgets. */

import { useCallback, useMemo, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { CitationWidget } from "@/components/ai-summary/CitationWidget";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import type { SummaryPoint } from "../../../convex/aiSummary";

/** Collect all unique feedItemIds from all citation segments. */
function collectCitationIds(points: SummaryPoint[]): Id<"feedItems">[] {
  const ids = new Set<string>();
  for (const point of points) {
    if (point.segments) {
      for (const seg of point.segments) {
        for (const c of seg.citations) ids.add(c.feedItemId);
      }
    }
    if (point.refToCitations) {
      for (const arr of Object.values(point.refToCitations)) {
        for (const c of arr) ids.add(c.feedItemId);
      }
    }
  }
  return [...ids] as Id<"feedItems">[];
}

interface SummaryPointsListProps {
  points: SummaryPoint[];
  hoveredSegmentKey: string | null;
  onHoverSegment: (key: string | null) => void;
}

export function SummaryPointsList({ points, hoveredSegmentKey, onHoverSegment }: SummaryPointsListProps) {
  const moveToQueue = useMutation(api.feedItems.moveToQueue);

  const allCitationIds = useMemo(() => collectCitationIds(points), [points]);
  const backendQueuedIds = useQuery(
    api.feedItems.getQueuedIds,
    allCitationIds.length > 0 ? { ids: allCitationIds } : "skip",
  );
  const queuedIds = useMemo(
    () => new Set(backendQueuedIds ?? []),
    [backendQueuedIds],
  );

  const handleQueue = useCallback((e: React.MouseEvent, feedItemId: string) => {
    e.preventDefault();
    e.stopPropagation();
    moveToQueue({ id: feedItemId as Id<"feedItems"> });
  }, [moveToQueue]);

  return (
    <ul className="space-y-3">
      {points.map((point, pointIdx) => (
        <li key={pointIdx}>
          <PointContent
            point={point}
            pointIdx={pointIdx}
            hoveredSegmentKey={hoveredSegmentKey}
            onHoverSegment={onHoverSegment}
            queuedIds={queuedIds}
            onQueue={handleQueue}
          />
        </li>
      ))}
    </ul>
  );
}

interface PointContentProps {
  point: SummaryPoint;
  pointIdx: number;
  hoveredSegmentKey: string | null;
  onHoverSegment: (key: string | null) => void;
  queuedIds: Set<string>;
  onQueue: (e: React.MouseEvent, feedItemId: string) => void;
}

function PointContent({ point, pointIdx, hoveredSegmentKey, onHoverSegment, queuedIds, onQueue }: PointContentProps) {
  // Modern format: segments array
  if (point.segments && point.segments.length > 0) {
    return (
      <p className="text-sm leading-relaxed">
        {point.segments.map((segment, segIdx) => {
          const key = `${pointIdx}-${segIdx}`;
          const hasCitations = segment.citations.length > 0;

          if (!hasCitations) {
            const isTopicLabel = segIdx === 0 && segment.text.trimEnd().endsWith(":");
            if (isTopicLabel) {
              const label = segment.text.trim().replace(/:$/, "");
              return (
                <span key={key}>
                  <Badge variant="outline" className="mr-1.5 align-middle text-[11px] px-1.5 py-0 h-[18px] text-primary/80 border-primary/30">
                    {label}
                  </Badge>
                </span>
              );
            }
            return <span key={key}>{segment.text}</span>;
          }

          return (
            <CitationSegment
              key={key}
              segmentKey={key}
              text={segment.text}
              citations={segment.citations}
              isHovered={hoveredSegmentKey === key}
              queuedIds={queuedIds}
              onQueue={onQueue}
              onHoverSegment={onHoverSegment}
            />
          );
        })}
      </p>
    );
  }

  // Legacy format: text + refToCitations
  if (point.text) {
    return <p className="text-sm leading-relaxed">{point.text}</p>;
  }

  return null;
}

interface CitationSegmentProps {
  segmentKey: string;
  text: string;
  citations: import("../../../convex/aiSummary").SummaryCitation[];
  isHovered: boolean;
  queuedIds: Set<string>;
  onQueue: (e: React.MouseEvent, feedItemId: string) => void;
  onHoverSegment: (key: string | null) => void;
}

function CitationSegment({ segmentKey, text, citations, isHovered, queuedIds, onQueue, onHoverSegment }: CitationSegmentProps) {
  const anchorRef = useRef<HTMLSpanElement>(null);

  return (
    <span className="relative inline">
      <span
        ref={anchorRef}
        className="cursor-pointer rounded-sm bg-primary/25 px-0.5 transition-colors hover:bg-primary/35"
        onMouseEnter={() => onHoverSegment(segmentKey)}
        onMouseLeave={() => onHoverSegment(null)}
      >
        {text}
      </span>
      {isHovered && (
        <CitationWidget
          citations={citations}
          queuedIds={queuedIds}
          onQueue={onQueue}
          anchorRef={anchorRef}
          onMouseEnter={() => onHoverSegment(segmentKey)}
          onMouseLeave={() => onHoverSegment(null)}
        />
      )}
    </span>
  );
}
